'use client';

import { useMemo } from 'react';

import {
  SPECIALITY_PAGE_MAPPING,
  specialityNamesMatch,
} from '@/features/search';

import type { DoctorSuggestion } from '../types';
import {
  ResultColumnEmpty,
  ResultColumnHeader,
  resultRowClassName,
} from './resultColumnShared';

interface DoctorsColumnProps {
  doctors: DoctorSuggestion[];
  activeIndex: number;
  flatIndexBySlug: Map<string, number>;
  hoveredSpecialitySlug: string | null;
  isCodeSearch: boolean;
  onHoverDoctor: (flatIndex: number) => void;
  onClearHover: () => void;
  onSelect: (url: string) => void;
}

/** Resolves the hovered speciality row + the names it covers. */
function useHoveredScope(hoveredSpecialitySlug: string | null) {
  return useMemo(() => {
    const hoveredRow = hoveredSpecialitySlug
      ? SPECIALITY_PAGE_MAPPING.find(
          (row) => row.path === hoveredSpecialitySlug,
        )
      : undefined;
    const hoveredNames = hoveredRow
      ? [
          hoveredRow.pageName,
          hoveredRow.primarySpeciality,
          ...hoveredRow.relevantSpecialities,
        ]
      : hoveredSpecialitySlug
        ? [hoveredSpecialitySlug]
        : [];
    return {
      hoveredRow,
      hoveredNames,
      hoveredTitle: hoveredRow?.pageName ?? hoveredSpecialitySlug,
    };
  }, [hoveredSpecialitySlug]);
}

/**
 * Middle column: consultants/specialists. While a speciality is hovered the
 * list narrows to that speciality's specialists (never falls back to the
 * full list mid-hover); the header offers "Show all" to clear the hover.
 */
export default function DoctorsColumn({
  doctors,
  activeIndex,
  flatIndexBySlug,
  hoveredSpecialitySlug,
  isCodeSearch,
  onHoverDoctor,
  onClearHover,
  onSelect,
}: DoctorsColumnProps) {
  const { hoveredNames, hoveredTitle } = useHoveredScope(hoveredSpecialitySlug);

  const displayedDoctors = useMemo(() => {
    if (!hoveredSpecialitySlug) return doctors;
    // Always narrow to the hovered speciality's specialists;
    // never fall back to the full list while hovering.
    return doctors.filter((doc) => {
      const docNames = [
        doc.primarySpeciality ?? '',
        ...(doc.specialities ?? []),
      ];
      return docNames.some((doctorName) =>
        hoveredNames.some((hoveredName) =>
          specialityNamesMatch(doctorName, hoveredName),
        ),
      );
    });
  }, [doctors, hoveredSpecialitySlug, hoveredNames]);

  const headerAction = hoveredSpecialitySlug ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClearHover();
      }}
      className="text-theme-primary text-caption cursor-pointer font-semibold normal-case hover:underline"
    >
      Show all
    </button>
  ) : undefined;

  return (
    <div className="md:max-h-searchbar-column-max-height flex min-h-0 min-w-0 flex-col overflow-y-auto">
      <ResultColumnHeader
        title={
          <>
            {isCodeSearch ? 'Consultants' : 'Specialists'}
            {hoveredTitle && (
              <span className="text-theme-primary ml-1 font-semibold normal-case">
                • {hoveredTitle}
              </span>
            )}
          </>
        }
        count={displayedDoctors.length}
        action={headerAction}
      />
      <div className="flex flex-col gap-1 p-1">
        {displayedDoctors.length === 0 ? (
          <ResultColumnEmpty>
            <span className="block px-5 py-6 text-center not-italic">
              {hoveredSpecialitySlug
                ? `No specialists found under ${hoveredTitle}`
                : 'No specialists found'}
            </span>
          </ResultColumnEmpty>
        ) : (
          displayedDoctors.map((doctor) => {
            const flatIndex =
              flatIndexBySlug.get(`doctor:${doctor.slug}`) ?? -1;
            const isActive = flatIndex === activeIndex;
            return (
              <button
                key={`${doctor.type}-${doctor.slug}`}
                id={`option-${doctor.type}-${doctor.slug}`}
                role="option"
                aria-selected={isActive}
                type="button"
                onMouseEnter={() => onHoverDoctor(flatIndex)}
                onClick={() => onSelect(doctor.url)}
                className={`flex items-center justify-between ${resultRowClassName(isActive)}`}
              >
                <div className="flex flex-col">
                  <span className="font-bold text-slate-800">
                    {doctor.fullName}
                  </span>
                  {doctor.heading && (
                    <span className="text-caption mt-0.5 line-clamp-1 text-slate-500">
                      {doctor.heading}
                    </span>
                  )}
                </div>
                {doctor.primarySpeciality && (
                  <span className="text-theme-primary text-caption ml-4 shrink-0 rounded-full bg-slate-100 px-2 py-0.5 font-bold">
                    {doctor.primarySpeciality}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
